import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Quote } from "lucide-react";
import { FOUNDER_PORTRAIT } from "../lib/data";
import { useContent } from "../context/ContentContext";

export const FounderTeaser = () => {
  const { content } = useContent();
  const story = content?.story || {};
  const portrait = story.portrait_url || FOUNDER_PORTRAIT;
  const quote =
    story.quote ||
    "Straßenwert ist aus einer echten Freundschaft entstanden — aus Leidenschaft für Musik, gemeinsamen Visionen und dem Willen, etwas Eigenes aufzubauen.";

  return (
    <section
      id="gruender"
      data-testid="founder-teaser"
      className="relative bg-[#050505] py-28 md:py-40 overflow-hidden grain"
    >
      {/* Ambient red glow */}
      <div className="absolute top-1/2 -translate-y-1/2 left-1/4 w-[36rem] h-[36rem] rounded-full bg-[#ff0033]/8 blur-[160px] pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px hairline-red opacity-40" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Portrait */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.2, 0.8, 0.2, 1] }}
            className="lg:col-span-5"
          >
            <Link
              to="/geschichte"
              data-testid="founder-portrait-link"
              className="relative block aspect-[4/5] overflow-hidden border border-white/[0.08] group"
            >
              <img
                src={portrait}
                alt="Maurizio Rogait — Gründer Straßenwert"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.04] transition-all duration-[1400ms] ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent pointer-events-none" />
              <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-[#ff0033]/40 blur-3xl group-hover:bg-[#ff0033]/60 transition-colors duration-700" />
              {/* Caption */}
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 z-10">
                <div className="flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-[#ff0033] font-bold mb-2">
                  <span className="block w-6 h-px bg-[#ff0033] shadow-[0_0_8px_#ff0033]" />
                  Gründer
                </div>
                <div className="font-display font-black uppercase text-2xl md:text-3xl tracking-tight">
                  Maurizio Rogait
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3 mb-8 text-[10px] md:text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
              <span className="block w-10 h-px bg-[#ff0033] shadow-[0_0_10px_#ff0033]" />
              Die Geschichte hinter Straßenwert
            </div>

            <h2
              data-testid="founder-headline"
              className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-5xl lg:text-6xl"
            >
              Vom Block <br />
              <span className="text-glow-red-strong text-white">ins Studio.</span>
            </h2>

            <div className="relative mt-10 border-l-2 border-[#ff0033] pl-5 md:pl-7 max-w-2xl">
              <Quote size={22} className="text-[#ff0033] mb-3 -ml-1 opacity-60" />
              <p className="font-display text-lg md:text-xl text-zinc-200 leading-relaxed italic">
                „{quote}"
              </p>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/geschichte"
                data-testid="founder-cta"
                className="group relative inline-flex items-center gap-3 bg-white text-black px-7 py-4 text-[10px] md:text-[11px] font-bold tracking-[0.3em] uppercase overflow-hidden hover:shadow-[0_0_28px_rgba(255,0,51,0.45)] transition-all"
              >
                <span className="relative z-10 group-hover:text-white transition-colors">
                  Die ganze Geschichte
                </span>
                <ArrowUpRight
                  size={14}
                  className="relative z-10 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                />
                <span className="absolute inset-0 bg-[#ff0033] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FounderTeaser;
